import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'sonner';
import './i18n';
import '@/App.css';

import Preloader from './components/Preloader';
import SmoothScroll from './components/SmoothScroll';
import ScrollToTop from './components/ScrollToTop';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import SportsBackground from './components/SportsBackground';
import Seo from './components/Seo';

import HomePage from './pages/HomePage';
import DonatePage from './pages/DonatePage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import AboutPage from './pages/AboutPage';
import GalleryPage from './pages/GalleryPage';
import ShopPage from './pages/ShopPage';
import CheckoutPage from './pages/CheckoutPageFixed';
import SuccessPage from './pages/SuccessPage';
import CancelPage from './pages/CancelPage';

// Inner component that has access to router hooks
function AppContent({ loading, setLoading, cartCount, setCartCount }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const browserLanguage = typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('bg') ? 'bg' : 'en';

  const handleCartClick = () => {
    const isShopPage = location.pathname.includes('/shop');
    if (!isShopPage) {
      navigate(`/${i18n.language}/shop`);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('openCart'));
      }, 500);
    } else {
      window.dispatchEvent(new CustomEvent('openCart'));
    }
  };

  useEffect(() => {
    const path = window.location.pathname;
    const langMatch = path.match(/^\/(bg|en)/);
    i18n.changeLanguage(langMatch ? langMatch[1] : browserLanguage);
  }, [browserLanguage, i18n]);

  useEffect(() => {
    const updateCartCount = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('cart') || '[]');
        setCartCount(cart.reduce((total, item) => total + Math.max(0, Number(item.quantity) || 0), 0));
      } catch {
        setCartCount(0);
      }
    };

    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, [setCartCount]);

  if (loading) {
    return <Preloader onComplete={() => setLoading(false)} />;
  }

  return (
    <>
      <SmoothScroll disabled={location.pathname.includes('/gallery')}>
        <Seo />
        <ScrollToTop />
        <SportsBackground />
        <div className="App relative z-10">
          <Navigation cartCount={cartCount} onCartClick={handleCartClick} />
          <Routes>
            <Route path="/" element={<Navigate to={`/${browserLanguage}`} replace />} />
            <Route path="/:lang" element={<HomePage />} />
            <Route path="/:lang/about" element={<AboutPage />} />
            <Route path="/:lang/events" element={<EventsPage />} />
            <Route path="/:lang/events/:eventId" element={<EventDetailPage />} />
            <Route path="/:lang/gallery" element={<GalleryPage />} />
            <Route path="/:lang/shop" element={<ShopPage />} />
            <Route path="/:lang/checkout" element={<CheckoutPage />} />
            <Route path="/:lang/donate" element={<DonatePage />} />
            <Route path="/:lang/success" element={<SuccessPage />} />
            <Route path="/:lang/cancel" element={<CancelPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/cancel" element={<CancelPage />} />
          </Routes>
          <Footer />
          <Toaster position="top-right" />
        </div>
      </SmoothScroll>
    </>
  );
}

// Main App component
function App() {
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);

  return (
    <BrowserRouter>
      <AppContent 
        loading={loading} 
        setLoading={setLoading} 
        cartCount={cartCount} 
        setCartCount={setCartCount} 
      />
    </BrowserRouter>
  );
}

export default App;


